import { GraphQLNonNull, GraphQLID, GraphQLFieldConfig } from 'graphql'
import {
  OrganizationApplicationsSettings,
  organizationApplicationsSettingsType
} from '../types/organization-applications-settings'

interface Context {
  readonly connector: {
    readonly get: (url: string) => Promise<any>
  }
}

interface Args {
  readonly organizationID: string
}

export const organizationApplicationsSettings: GraphQLFieldConfig<
  undefined,
  Context,
  Args
> = {
  description: 'Get the applications settings for an organization',
  type: new GraphQLNonNull(organizationApplicationsSettingsType),
  args: {
    organizationID: {
      description: 'The unique ID for the organization being queried',
      type: new GraphQLNonNull(GraphQLID)
    }
  },
  async resolve(
    _source,
    args,
    context
  ): Promise<OrganizationApplicationsSettings> {
    const { organizationID } = args

    const {
      settings: {
        importance,
        remediation_policies: remediationPolicies,
        compliance_policies: compliancePolicies,
        auto_license: autoLicense,
        filter_app_most_recent_session: filterAppMostRecentSession,
        prevent_onboarding_failing_applications:
          preventOnboardingFailingApplications,
        prevent_onboarding_failing_applications_by_session:
          preventOnboardingFailingApplicationsBySession,
        protect_environment_displayed_in_vulnerability_grid: protectRuleEnv,
        route_expiration_days: routeExpirationDays,
        route_coverage_absolute_policy
      }
    } = await context.connector.get(`${organizationID}/application/settings`)

    return {
      id: organizationID,
      importance: importance || 'MEDIUM',
      remediationPolicies: remediationPolicies.map(transformPolicy),
      compliancePolicies: compliancePolicies.map(transformPolicy),
      autoLicense,
      filterAppMostRecentSession,
      preventOnboardingFailingApplications,
      preventOnboardingFailingApplicationsBySession,
      protectRuleEnv: protectRuleEnv || 'PRODUCTION', // failsafe default
      routeExpirationDays,
      routeCoverageAbsolutePolicy: {
        id: route_coverage_absolute_policy?.id,
        name: route_coverage_absolute_policy?.name,
        coveragePercentage: route_coverage_absolute_policy?.coverage_percentage,
        applicationImportances:
          route_coverage_absolute_policy?.application_importances,
        applicationIds: route_coverage_absolute_policy?.application_ids,
        enabled: route_coverage_absolute_policy?.enabled,
        allApplications: route_coverage_absolute_policy?.all_applications
      }
    }
  }
}

function transformPolicy({ name, policy_id: id }) {
  return { id, name }
}
